import {
	reactive,
	ref,
	Ref,
	toRef,
	watch,
} from '@vue/composition-api';
import axios, { CancelTokenSource } from 'axios';
import { STATUS } from '@front-app/constants/state';
import { ContentType, ContentV2 } from '@front-app/services/GroupsApi/DiscussionModel';
import { ContentPageParamsV2, getGroupContentV2, getGroupContentsV2 } from '@front-app/services/GroupsApi/GroupsContentApi';
import { canSeeRemovedDiscussionTopic } from '@front-app/util/permission/discussion';
import { isRemovedDiscussion } from '@front-app/util/content';
import { useGroupContentStore } from '@front-app/store/piniaModules/groupContent';
import { STARTABLE_STATUSES } from '../util/cache';

const CANCEL_MESSAGE = 'Cancelled by user';

export interface GroupContentStore {
	hasMorePages: Ref<boolean>;
	contents: Ref<ContentV2[]>;
	status: Ref<STATUS>;
	cancelSearch(): void;
	fetchNextPage(): Promise<void>;
	resetToFirstPage(): Promise<void>;
	fetchAttachedTo(id: string): Promise<void>;
	update(newGroupContent: ContentV2[]): void;
	isFetchingAttachedTo: Ref<boolean>;
}

const PRE_FIRST_PAGE = 0;

export const getGroupContentStore = (
	currentGroupId: Ref<string>,
	params: Ref<ContentPageParamsV2>,
): GroupContentStore => {
	let source: CancelTokenSource;
	let shouldClearContents = true;
	let bufferedFileIds = '';
	let bufferedMessageIds = '';
	const state = reactive({
		currentPageNum: PRE_FIRST_PAGE,
		status: STATUS.UNSTARTED,
		contents: [] as ContentV2[],
		hasMorePages: true,
	});
	const update = (newGroupContent: ContentV2[]) => {
		newGroupContent.forEach((newContentItem) => {
			const updatedContentIndex = state.contents.findIndex((content) => content.id === newContentItem.id);
			const oldGroupContent = state.contents.filter(({ id }) => id !== newContentItem.id);
			oldGroupContent.splice(updatedContentIndex, 0, newContentItem);
			state.contents = oldGroupContent;
		})
	};
	const fetchNextPage = async () => {
		if (!STARTABLE_STATUSES.includes(state.status)) {
			return;
		}
		state.currentPageNum += 1;
		const groupId = currentGroupId.value;
		const pageNum = state.currentPageNum;

		state.status = STATUS.LOADING;
		const paramsWithMessageIds: ContentPageParamsV2 = {
			...params.value,
			bufferedMessageIds,
			bufferedFileIds,
		};
		source = axios.CancelToken.source();
		try {
			if (shouldClearContents) {
				state.contents = [];
				shouldClearContents = false;
			}
			const {
				contents,
				bufferedFileIds: newBufferedFileIds,
				bufferedMessageIds: newBufferedMessageIds,
			} = await getGroupContentsV2(groupId, pageNum, paramsWithMessageIds, source.token);

			const filteredContents = contents.filter((contentItem) => {
				// Filter out removed discussion topics that the user cannot see
				if (contentItem.type === ContentType.DISCUSSION && isRemovedDiscussion(contentItem) && !canSeeRemovedDiscussionTopic(contentItem)) {
					return false;
				}
				return true;
			});

			const groupContentStore = useGroupContentStore();
			groupContentStore.update(filteredContents);

			state.contents = state.contents.concat(filteredContents);
			bufferedFileIds = newBufferedFileIds;
			bufferedMessageIds = newBufferedMessageIds;
			state.status = STATUS.UNSTARTED;
			state.hasMorePages = filteredContents.length >= params.value.pageSize;
		} catch (e) {
			// eslint-disable-next-line no-console
			console.error(e);
			state.currentPageNum -= 1;
			if (isErrorWithMessage(e) && e.message === CANCEL_MESSAGE) {
				// In this case, the canceller is responsible for setting that new status
				return;
			}
			state.status = STATUS.ERROR;
		}
	};
	const cancelSearch = () => {
		if (source) {
			source.cancel(CANCEL_MESSAGE);
		}
	};
	const resetToFirstPage = async (): Promise<void> => {
		cancelSearch();
		state.currentPageNum = PRE_FIRST_PAGE;
		state.status = STATUS.UNSTARTED;
		shouldClearContents = true;
		bufferedFileIds = '';
		bufferedMessageIds = '';
		await fetchNextPage();
	};
	const resetSearchWatchHandler = (newVal: unknown, oldVal: unknown) => {
		if (newVal === oldVal) {
			return;
		}
		resetToFirstPage();
	};
	const isFetchingAttachedTo = ref(false);
	const fetchAttachedTo = async (contentId: string) => {
		const contentItem = state.contents.find(({ id }) => id === contentId);
		if (!contentItem) {
			// eslint-disable-next-line no-console
			console.error(`Attempted to update non-existent content: ${contentId}`);
			return;
		}

		// Discussions don't have "attachedToDiscussions", so skip it
		if (contentItem.type === ContentType.DISCUSSION) { return; }

		if (Array.isArray(contentItem.metadata.attachedToDiscussions)) { return; }
		isFetchingAttachedTo.value = true;
		const result = await getGroupContentV2(currentGroupId.value, contentId);

		// This is to satisfy typescript, even though we know that the result should have the
		// same type as above
		if (result.type === ContentType.DISCUSSION) {
			isFetchingAttachedTo.value = false;
			return;
		}
		const { attachedToDiscussions } = result.metadata;
		contentItem.metadata.attachedToDiscussions = attachedToDiscussions;
		isFetchingAttachedTo.value = false;
	};
	watch(currentGroupId, resetSearchWatchHandler);
	fetchNextPage();
	return {
		cancelSearch,
		fetchAttachedTo,
		fetchNextPage,
		resetToFirstPage,
		update,
		isFetchingAttachedTo,
		hasMorePages: toRef(state, 'hasMorePages'),
		status: toRef(state, 'status'),
		contents: toRef(state, 'contents'),
	};
};

interface HasMessage {
	message: string;
}

function isErrorWithMessage(item: unknown | HasMessage): item is HasMessage {
	return (item as HasMessage).message !== undefined;
}
